import { useEffect, useRef, useState } from "react"
import { Link, useFetcher, useLocation, useRouteLoaderData } from "@remix-run/react"
import { ArrowLeftIcon, Cross2Icon } from "@radix-ui/react-icons"
import { cn } from "~/utils/shadecn"
import {
  NO_PRICE_CHANGE,
  PRICE_ANNOUNCEMENT_PATH,
  PRICE_BAR_DISMISS,
  PRICE_DISMISS_ACTION,
  type PriceBannerState,
  type PriceChangeFlags,
} from "~/utils/price-change"

// The Nov 23 price-change messaging. Whether any of it shows is decided on the server
// (showPriceChangeMessaging, via the root loader); these components only read the result.

export function usePriceChange(): PriceChangeFlags {
  const root = useRouteLoaderData("root") as { priceChange?: PriceChangeFlags } | undefined
  return root?.priceChange ?? NO_PRICE_CHANGE
}

// The top bar is fixed above the (fixed) nav, so the nav moves down by its height and the page gets
// a spacer of the same height. One set of class strings per state so the three can't drift apart.
// "full" is the two-line banner from `sm` up, and the compact bar below it.
export const PRICE_BANNER_HEIGHT = { full: "h-7 sm:h-12", compact: "h-7" } as const
export const PRICE_BANNER_NAV_OFFSET = { full: "top-7 sm:top-12", compact: "top-7" } as const

/** What the top bar shows on this page view. Starts from the root loader's `banner`, which doesn't
 * re-run on every client-side navigation, so it also catches up with this page session: opening the
 * announcement shrinks it, and the X hides it at once while the fetcher records it. */
export function usePriceBanner(server: PriceBannerState) {
  const { pathname } = useLocation()
  const fetcher = useFetcher()
  const [readHere, setReadHere] = useState(false)
  const [dismissedHere, setDismissedHere] = useState(false)
  const dismissing = useRef(false)

  useEffect(() => {
    if (pathname === PRICE_ANNOUNCEMENT_PATH) setReadHere(true)
  }, [pathname])

  // Never on the announcement itself: the bar only points there.
  const state: PriceBannerState =
    server === "off" || dismissedHere || pathname === PRICE_ANNOUNCEMENT_PATH ? "off"
    : server === "full" && readHere ? "compact"
    : server

  const dismiss = () => {
    if (dismissing.current) return
    dismissing.current = true
    setDismissedHere(true)
    fetcher.submit({ dismiss: PRICE_BAR_DISMISS }, { method: "post", action: PRICE_DISMISS_ACTION })
  }

  return { state, dismiss }
}

/** The one bar above the site header. "full": the two-line banner, not dismissible, from `sm` up;
 * phones get the compact bar instead. "compact": one line with an X. Gone after the cutoff. */
export function PriceChangeBanner({ state, onDismiss }: { state: PriceBannerState; onDismiss: () => void }) {
  if (state === "off") return null
  return (
    <div
      data-testid="price-change-banner"
      data-state={state}
      className={cn("fixed inset-x-0 top-0 z-30 bg-primary text-zinc-50", PRICE_BANNER_HEIGHT[state])}>
      <PriceChangeBar onDismiss={onDismiss} className={state === "full" ? "sm:hidden" : undefined} />
      {state === "full" && (
        <div
          data-testid="price-change-banner-full"
          className="hidden h-full items-center justify-center px-2 text-center sm:flex">
          <p className="text-balance text-sm leading-5">
            <b>Our first price change in 14 years – Starting Nov 23</b>
            <br />
            Lock in your current rate today –{" "}
            <Link to={PRICE_ANNOUNCEMENT_PATH} className="font-semibold underline underline-offset-2 hover:no-underline">
              Learn more
            </Link>
          </p>
        </div>
      )}
    </div>
  )
}

function PriceChangeBar({ onDismiss, className }: { onDismiss: () => void; className?: string }) {
  return (
    <div
      data-testid="price-change-bar"
      className={cn("relative flex h-full items-center justify-center px-8 text-center", className)}>
      <p className="truncate text-xs leading-4">
        Prices change on 11/23 –{" "}
        <Link to={PRICE_ANNOUNCEMENT_PATH} className="font-semibold underline underline-offset-2 hover:no-underline">
          learn more
        </Link>
      </p>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        className="absolute right-1 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded opacity-80 hover:opacity-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-zinc-50">
        <Cross2Icon aria-hidden className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

/** Orange block under the header of the subscribe and bundle pages. */
export function PriceChangeNotice({ className }: { className?: string }) {
  const { messaging } = usePriceChange()
  if (!messaging) return null
  return (
    <div
      data-testid="price-change-notice"
      className={cn(
        "mx-4 max-w-3xl rounded-lg bg-[#A9532F] px-5 py-4 text-center text-[15px] leading-6 text-white sm:mx-auto sm:px-8",
        className
      )}>
      <p className="font-bold">On Nov 23, these prices increase for the first time in 14 years.</p>
      <p>Lock in your current rate for 12 months by upgrading to an annual subscription.</p>
      <p>
        Learn more about the price increase{" "}
        <Link to={PRICE_ANNOUNCEMENT_PATH} className="font-semibold underline underline-offset-2 hover:text-[#F2E4DD]">
          here
        </Link>
        .
      </p>
    </div>
  )
}

/** Arrow pointing left at the "Yearly" tab of /subscribe's monthly/yearly toggle. Place it in a
 * `relative` wrapper the width of the toggle; it sits just outside the wrapper's right edge. */
export function YearlyLockInCallout() {
  const { messaging } = usePriceChange()
  if (!messaging) return null
  return (
    <div
      data-testid="yearly-lock-in-callout"
      className="absolute left-full top-1/2 ml-2 flex w-24 -translate-y-1/2 items-center gap-1 text-xs font-semibold leading-4 text-[#A9532F] dark:text-[#E08A67] sm:w-auto sm:whitespace-nowrap sm:text-sm">
      <ArrowLeftIcon aria-hidden className="h-4 w-4 flex-none" />
      <span>Lock in today's price</span>
    </div>
  )
}
